
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as config from '../config'
import * as history from '../history'
import * as client from '../client'
import * as feed from '../feed'
import * as storage from '../storage'
import * as exposed from '../exposed'
import * as execution from '../execution'
import * as backup from '../backup'
import * as inner from '../inner'
import * as trade from '../trade'
import * as security from '../security'
import * as document from '../document'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {ConfigUpdateRequest} from './ConfigUpdateRequest';
import {FieldModelConfigUpdateRequest} from './ConfigUpdateRequest';
import {SymbolMapModifyResponse} from './SymbolMapModifyResponse';

/**
 * SymbolMapModifyRequest struct
 */
class SymbolMapModifyRequest extends ConfigUpdateRequest {
  /**
   * Initialize struct
   * @param {!ConfigUpdateRequest=} parent
   * @param {!UUID=} symbolMapId
   * @param {string=} newName
   * @param {!Map=} symbolsToModify
   * @param {!Set=} symbolsToDelete
   * @param {string=} newDescription
   * @constructor
   */
  constructor (parent = new ConfigUpdateRequest(), symbolMapId = new UUID(), newName = null, symbolsToModify = new Map(), symbolsToDelete = new Set(), newDescription = null) {
    super()
    super.copy(parent)
    this.SymbolMapId = symbolMapId
    this.NewName = newName
    this.SymbolsToModify = symbolsToModify
    this.SymbolsToDelete = symbolsToDelete
    this.NewDescription = newDescription
  }

  /**
   * Copy struct (shallow copy)
   * @this {!SymbolMapModifyRequest}
   * @param {!SymbolMapModifyRequest} other Other struct
   * @returns {!SymbolMapModifyRequest} This struct
   */
  copy (other) {
    super.copy(other)
    if (other.SymbolMapId != null) {
      this.SymbolMapId = new UUID(other.SymbolMapId)
    } else {
      this.SymbolMapId = null
    }
    if (other.NewName != null) {
      this.NewName = other.NewName
    } else {
      this.NewName = null
    }
    if (other.SymbolsToModify != null) {
      this.SymbolsToModify = new Map()
      Object.keys(other.SymbolsToModify).forEach(key => {
        if (key != null) {
          let tempKey
          tempKey = key
          if (other.SymbolsToModify[key] != null) {
            let tempValue
            tempValue = other.SymbolsToModify[key]
            this.SymbolsToModify.set(tempKey, tempValue)
          } else {
            this.SymbolsToModify.set(tempKey, null)
          }
        }
      })
    } else {
      this.SymbolsToModify = null
    }
    if (other.SymbolsToDelete != null) {
      this.SymbolsToDelete = new Set()
      for (let item of other.SymbolsToDelete) {
        if (item != null) {
          let tempItem
          tempItem = item
          this.SymbolsToDelete.add(tempItem)
        } else {
          this.SymbolsToDelete.add(null)
        }
      }
    } else {
      this.SymbolsToDelete = null
    }
    if (other.NewDescription != null) {
      this.NewDescription = other.NewDescription
    } else {
      this.NewDescription = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!SymbolMapModifyRequest}
   * @returns {!SymbolMapModifyRequest} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new SymbolMapModifyRequestModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new SymbolMapModifyRequestModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!SymbolMapModifyRequest}
   * @param {!SymbolMapModifyRequest} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof SymbolMapModifyRequest)) {
      throw new TypeError('Instance of SymbolMapModifyRequest is required!')
    }
    // noinspection RedundantIfStatementJS
    if (!super.eq(other)) {
      return false
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!SymbolMapModifyRequest}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    let parent = super.toJSON()
    let current = {
      SymbolMapId: ((this.SymbolMapId != null) ? this.SymbolMapId.toString() : null),
      NewName: ((this.NewName != null) ? this.NewName : null),
      SymbolsToModify: ((this.SymbolsToModify != null) ? fbe.MapToObject(new Map(Array.from(this.SymbolsToModify, item => [((item[0] != null) ? item[0] : null), ((item[1] != null) ? item[1] : null)]))) : null),
      SymbolsToDelete: ((this.SymbolsToDelete != null) ? Array.from(this.SymbolsToDelete, item => ((item != null) ? item : null)) : null),
      NewDescription: ((this.NewDescription != null) ? this.NewDescription : null)
    }
    return { ...parent, ...current }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return SymbolMapModifyRequest.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!SymbolMapModifyRequest} other Object value
   * @returns {!SymbolMapModifyRequest} Created struct
   */
  static fromObject (other) {
    return new SymbolMapModifyRequest().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!SymbolMapModifyRequest}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return SymbolMapModifyRequest.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!SymbolMapModifyRequest}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 6319
  }
}

export { SymbolMapModifyRequest };

/**
 * Fast Binary Encoding SymbolMapModifyRequest field model
 */
class FieldModelSymbolMapModifyRequest extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this._parent = new FieldModelConfigUpdateRequest(buffer, 4 + 4)
    this.SymbolMapId = new fbe.FieldModelUUID(buffer, this._parent.fbeOffset + this._parent.fbeBody - 4 - 4)
    this.NewName = new fbe.FieldModelOptional(new fbe.FieldModelString(buffer, this.SymbolMapId.fbeOffset + this.SymbolMapId.fbeSize), buffer, this.SymbolMapId.fbeOffset + this.SymbolMapId.fbeSize)
    this.SymbolsToModify = new fbe.FieldModelMap(new fbe.FieldModelString(buffer, this.NewName.fbeOffset + this.NewName.fbeSize), new fbe.FieldModelString(buffer, this.NewName.fbeOffset + this.NewName.fbeSize), buffer, this.NewName.fbeOffset + this.NewName.fbeSize)
    this.SymbolsToDelete = new fbe.FieldModelSet(new fbe.FieldModelString(buffer, this.SymbolsToModify.fbeOffset + this.SymbolsToModify.fbeSize), buffer, this.SymbolsToModify.fbeOffset + this.SymbolsToModify.fbeSize)
    this.NewDescription = new fbe.FieldModelOptional(new fbe.FieldModelString(buffer, this.SymbolsToDelete.fbeOffset + this.SymbolsToDelete.fbeSize), buffer, this.SymbolsToDelete.fbeOffset + this.SymbolsToDelete.fbeSize)
  }

  /**
   * Get the ConfigUpdateRequest field model
   * @this {!FieldModelSymbolMapModifyRequest}
   * @returns {!FieldModelConfigUpdateRequest} ConfigUpdateRequest field model
   */
  get parent () {
    return this._parent
  }

  /**
   * Get the field size
   * @this {!FieldModelSymbolMapModifyRequest}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelSymbolMapModifyRequest}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.parent.fbeBody - 4 - 4 + this.SymbolMapId.fbeSize + this.NewName.fbeSize + this.SymbolsToModify.fbeSize + this.SymbolsToDelete.fbeSize + this.NewDescription.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelSymbolMapModifyRequest}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.parent.fbeExtra + this.SymbolMapId.fbeExtra + this.NewName.fbeExtra + this.SymbolsToModify.fbeExtra + this.SymbolsToDelete.fbeExtra + this.NewDescription.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelSymbolMapModifyRequest}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelSymbolMapModifyRequest.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelSymbolMapModifyRequest}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 6319
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelSymbolMapModifyRequest}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelSymbolMapModifyRequest}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) > fbeStructSize) {
      return true
    }
    if (!this.parent.verifyFields(fbeStructSize)) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.SymbolMapId.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.SymbolMapId.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.SymbolMapId.fbeSize

    if ((fbeCurrentSize + this.NewName.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.NewName.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.NewName.fbeSize

    if ((fbeCurrentSize + this.SymbolsToModify.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.SymbolsToModify.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.SymbolsToModify.fbeSize

    if ((fbeCurrentSize + this.SymbolsToDelete.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.SymbolsToDelete.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.SymbolsToDelete.fbeSize

    if ((fbeCurrentSize + this.NewDescription.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.NewDescription.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.NewDescription.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelSymbolMapModifyRequest}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelSymbolMapModifyRequest}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelSymbolMapModifyRequest}
   * @param {!SymbolMapModifyRequest} fbeValue Default value, defaults is new SymbolMapModifyRequest()
   * @returns {!SymbolMapModifyRequest} SymbolMapModifyRequest value
   */
  get (fbeValue = new SymbolMapModifyRequest()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelSymbolMapModifyRequest}
   * @param {!SymbolMapModifyRequest} fbeValue SymbolMapModifyRequest value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) <= fbeStructSize) {
      this.parent.getFields(fbeValue, fbeStructSize)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.SymbolMapId.fbeSize) <= fbeStructSize) {
      fbeValue.SymbolMapId = this.SymbolMapId.get()
    } else {
      fbeValue.SymbolMapId = new UUID()
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.SymbolMapId.fbeSize

    if ((fbeCurrentSize + this.NewName.fbeSize) <= fbeStructSize) {
      fbeValue.NewName = this.NewName.get()
    } else {
      fbeValue.NewName = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.NewName.fbeSize

    if ((fbeCurrentSize + this.SymbolsToModify.fbeSize) <= fbeStructSize) {
      this.SymbolsToModify.get(fbeValue.SymbolsToModify)
    } else {
      fbeValue.SymbolsToModify.clear()
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.SymbolsToModify.fbeSize

    if ((fbeCurrentSize + this.SymbolsToDelete.fbeSize) <= fbeStructSize) {
      this.SymbolsToDelete.get(fbeValue.SymbolsToDelete)
    } else {
      fbeValue.SymbolsToDelete.clear()
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.SymbolsToDelete.fbeSize

    if ((fbeCurrentSize + this.NewDescription.fbeSize) <= fbeStructSize) {
      fbeValue.NewDescription = this.NewDescription.get()
    } else {
      fbeValue.NewDescription = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.NewDescription.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelSymbolMapModifyRequest}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelSymbolMapModifyRequest}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelSymbolMapModifyRequest}
   * @param {!SymbolMapModifyRequest} fbeValue SymbolMapModifyRequest value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelSymbolMapModifyRequest}
   * @param {SymbolMapModifyRequest} fbeValue SymbolMapModifyRequest value
   */
  setFields (fbeValue) {
    this.parent.setFields(fbeValue)
    this.SymbolMapId.set(fbeValue.SymbolMapId)
    this.NewName.set(fbeValue.NewName)
    this.SymbolsToModify.set(fbeValue.SymbolsToModify)
    this.SymbolsToDelete.set(fbeValue.SymbolsToDelete)
    this.NewDescription.set(fbeValue.NewDescription)
  }
}

export { FieldModelSymbolMapModifyRequest };

/**
 * Fast Binary Encoding SymbolMapModifyRequest model
 */
class SymbolMapModifyRequestModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelSymbolMapModifyRequest(this.buffer, 4)
  }

  /**
   * Get the SymbolMapModifyRequest model
   * @this {!SymbolMapModifyRequestModel}
   * @returns {!FieldModelSymbolMapModifyRequest} model SymbolMapModifyRequest model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!SymbolMapModifyRequestModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!SymbolMapModifyRequestModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return SymbolMapModifyRequestModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!SymbolMapModifyRequestModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelSymbolMapModifyRequest.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!SymbolMapModifyRequestModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!SymbolMapModifyRequestModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!SymbolMapModifyRequestModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!SymbolMapModifyRequestModel}
   * @param {!SymbolMapModifyRequest} value SymbolMapModifyRequest value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!SymbolMapModifyRequestModel}
   * @param {!SymbolMapModifyRequest} value SymbolMapModifyRequest value, defaults is new SymbolMapModifyRequest()
   * @return {!object} Deserialized SymbolMapModifyRequest value and its size
   */
  deserialize (value = new SymbolMapModifyRequest()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new SymbolMapModifyRequest(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new SymbolMapModifyRequest(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!SymbolMapModifyRequestModel}
   * @param {!number} prev Previous SymbolMapModifyRequest model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { SymbolMapModifyRequestModel };
SymbolMapModifyRequest.__has_response = true;
SymbolMapModifyRequest.__response_class = SymbolMapModifyResponse;
